import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import ChartCard from '../components/ChartCard';
import { chartSources } from '../data/chartSources';
import { TrendingFeed } from '../components/TrendingFeed';
import { NewsletterForm } from '../components/NewsletterForm';
import { LayoutGrid, List } from 'lucide-react';

type ViewMode = 'list' | 'grid';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  const renderChartList = () => (
    <div className="bg-white/80 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden border border-gray-100 dark:border-gray-700">
      <div className="divide-y divide-gray-200 dark:divide-gray-700">
        {chartSources.map((source) => {
          const Icon = source.logo;
          const isBillboardChart = source.id.startsWith('billboard');
          
          return (
            <div
              key={source.id}
              onClick={isBillboardChart ? () => navigate(`/charts/${source.id}`) : undefined}
              className={`p-6 flex items-center space-x-6 relative group transition-colors duration-200 ${
                isBillboardChart 
                  ? 'cursor-pointer hover:bg-indigo-50/50 dark:hover:bg-indigo-900/20' 
                  : 'opacity-75'
              }`}
            >
              <div className="flex-shrink-0">
                <div className={`p-3 rounded-xl ${
                  isBillboardChart 
                    ? 'bg-indigo-100 dark:bg-indigo-900/30 group-hover:bg-indigo-200 dark:group-hover:bg-indigo-900/40' 
                    : 'bg-gray-100 dark:bg-gray-700'
                } transition-colors duration-200`}>
                  <Icon className={`w-8 h-8 ${
                    isBillboardChart 
                      ? 'text-indigo-600 dark:text-indigo-400' 
                      : 'text-gray-400 dark:text-gray-500'
                  }`} />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className={`text-lg font-semibold ${
                  isBillboardChart 
                    ? 'text-gray-900 dark:text-white' 
                    : 'text-gray-600 dark:text-gray-400'
                }`}>
                  {source.name}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1 mt-1">
                  {source.description}
                </p>
              </div>
              {isBillboardChart ? (
                <div className="flex-shrink-0">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400 group-hover:bg-indigo-200 dark:group-hover:bg-indigo-900/40 transition-colors duration-200">
                    View Chart
                  </span>
                </div>
              ) : (
                <div className="flex-shrink-0">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400">
                    Coming Soon
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderChartGrid = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      {chartSources.map((source) => (
        <ChartCard
          key={source.id}
          source={source}
          onClick={() => navigate(`/charts/${source.id}`)}
        />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-900 dark:to-indigo-950">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 mb-12">
          <div>
            <h1 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-400 dark:to-purple-400">
              Music Charts
            </h1>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-400 max-w-3xl leading-relaxed">
              Discover the latest music trends from around the world. We aggregate charts from multiple
              sources to give you a comprehensive view of what's hot in music right now.
            </p>
          </div>
          <div className="flex items-center gap-2 bg-white/80 dark:bg-gray-800/90 backdrop-blur-sm p-1.5 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700">
            <button
              onClick={() => setViewMode('list')}
              className={`p-2.5 rounded-lg transition-all duration-200 ${
                viewMode === 'list'
                  ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50'
              }`}
              title="List view"
            >
              <List className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2.5 rounded-lg transition-all duration-200 ${
                viewMode === 'grid'
                  ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50'
              }`}
              title="Grid view"
            >
              <LayoutGrid className="w-5 h-5" />
            </button>
          </div>
        </div>
        {viewMode === 'list' ? renderChartList() : renderChartGrid()}
        <NewsletterForm />
      </main>
      <TrendingFeed preview={true} limit={10} />
    </div>
  );
};

export default HomePage;